import NextImage from "next/image";
import Link from "next/link";
import {
    Breadcrumbs as Modes0Breadcrumbs,
    ContentArticleReader,
    ContentTitles,
    PageWithSidebarLayout,
    ServicesTrustHero,
    SidebarArticle,
    type Crumb,
    type SidebarArticleItem,
} from "@modes0/ui-components";
import { PageSeo, buildBreadcrumbSchema, buildServiceSchema } from "@modes0/ui-components-seo";
import {
    WEBSITE_URL,
    leadGenerationBottomContactFormContent,
    leadGenerationContactFormContent,
    leadGenerationContentBlocks,
    leadGenerationHeroImage,
    leadGenerationMetaTags,
    leadGenerationRelatedServices,
    leadGenerationServiceAdvocate,
} from "@/content";
import { getBreadcrumbSeoItems, seoSiteProfile } from "@/content/seo";
import ServiceContactForm from "@/widgets/contact-form/ServiceContactForm";

const brandColor = "#008e0e";
const pageBackgroundColor = "#ffffff";

const crumbs: Crumb[] = [
    { title: "Home", href: "/" },
    { title: "Services", href: "/services" },
    { title: "Lead Generation Sydney" },
];

const breadcrumbItems = [
    {
        position: 1,
        name: "Home",
        item: WEBSITE_URL,
    },
    {
        position: 2,
        name: "Services",
        item: `${WEBSITE_URL}/services`,
    },
    {
        position: 3,
        name: "Lead Generation Sydney",
        item: leadGenerationMetaTags.url,
    },
];

const relatedItems: SidebarArticleItem[] = leadGenerationRelatedServices.map((service) => ({
    id: service.id,
    title: service.title,
    description: service.description,
    href: service.href,
    ctaLabel: "View service",
    accentColor: brandColor,
    image: {
        src: service.imageUrl,
        url: service.imageUrl,
        original: service.imageUrl,
        thumbnail: service.imageUrl,
        alt: service.title,
        title: service.title,
    },
}));

const LeadGenerationMainContent = () => {
    const [introContentBlock, ...remainingContentBlocks] = leadGenerationContentBlocks;

    return (
        <div className="min-w-0 bg-white px-4 py-5 md:px-6 md:py-6">
            <div className="mb-6">
                <Modes0Breadcrumbs crumbs={crumbs} currentPageAccent={brandColor} />
            </div>
            <article className="min-w-0">
                <ContentTitles
                    title="Lead Generation Sydney"
                    subTitle="Qualified enquiries through websites, SEO, Google Ads, landing pages, Google Business Profile and tracking."
                    titleComponent="h1"
                    subTitleComponent="h2"
                    titleClasses="text-4xl md:text-5xl"
                    subTitleClasses="text-lg md:text-xl"
                />
                <div className="mt-6 min-w-0">
                    <ServicesTrustHero
                        Link={Link}
                        skyline={false}
                        image={{
                            src: leadGenerationHeroImage.url,
                            url: leadGenerationHeroImage.url,
                            original: leadGenerationHeroImage.original,
                            thumbnail: leadGenerationHeroImage.thumbnail,
                            alt: leadGenerationHeroImage.alt,
                            title: leadGenerationHeroImage.title,
                            loading: "lazy",
                        }}
                        badgeLabel={leadGenerationServiceAdvocate.badgeLabel}
                        badgeText={leadGenerationServiceAdvocate.badgeText}
                        title={leadGenerationServiceAdvocate.title}
                        titleHighlight={leadGenerationServiceAdvocate.titleHighlight}
                        titleComponent="h2"
                        lead={leadGenerationServiceAdvocate.lead}
                        description={leadGenerationServiceAdvocate.description}
                        benefits={leadGenerationServiceAdvocate.benefits}
                        primaryAction={leadGenerationServiceAdvocate.primaryAction}
                        secondaryAction={leadGenerationServiceAdvocate.secondaryAction}
                        review={false}
                        trustItems={leadGenerationServiceAdvocate.trustItems}
                        accentColor={brandColor}
                        backgroundColor="transparent"
                        surfaceColor="#ffffff"
                        maxWidth="100%"
                        className="service-page-advocate"
                        ariaLabel={leadGenerationServiceAdvocate.ariaLabel}
                    />
                    {introContentBlock && (
                        <ContentArticleReader contentBlocks={[introContentBlock] as never} paddingY="18px" />
                    )}
                    <figure className="my-8 overflow-hidden rounded-lg border border-black/10 bg-white shadow-sm">
                        <div className="relative h-[260px] w-full md:h-[420px]">
                            <NextImage
                                src={leadGenerationHeroImage.url}
                                alt={leadGenerationHeroImage.alt}
                                fill
                                sizes="(min-width: 1024px) 820px, calc(100vw - 32px)"
                                className="object-cover"
                                loading="lazy"
                            />
                        </div>
                        <figcaption className="border-t border-black/10 px-4 py-3 text-sm leading-6 text-neutral-700">
                            {leadGenerationHeroImage.caption}
                        </figcaption>
                    </figure>
                    {remainingContentBlocks.length > 0 && (
                        <ContentArticleReader contentBlocks={remainingContentBlocks as never} paddingY="18px" />
                    )}
                </div>
            </article>
            <div id="contact" className="mt-10 border-t border-black/10 pt-8">
                <ServiceContactForm content={leadGenerationContactFormContent} />
            </div>
        </div>
    );
};

const LeadGenerationSidebar = () => (
    <div className="space-y-6">
        <SidebarArticle
            items={relatedItems}
            Link={Link}
            title="Related services"
            titleComponent="h2"
            description="Connect the lead-generation page with the services that support enquiry growth."
            itemCtaLabel="View service"
            accentColor={brandColor}
            maxWidth="100%"
            imageWidth={82}
            imageHeight={68}
            ariaLabel="Lead generation related services"
        />
    </div>
);

export default function LeadGenerationSydneyPage() {
    return (
        <>
            <PageSeo
                meta={leadGenerationMetaTags}
                siteProfile={seoSiteProfile}
                schemas={[
                    {
                        id: "lead-generation-sydney-service-json-ld",
                        schema: buildServiceSchema(
                            {
                                name: "Lead Generation Sydney",
                                description: leadGenerationMetaTags.description,
                                serviceType: "Lead Generation Sydney",
                                url: leadGenerationMetaTags.url,
                                image: leadGenerationMetaTags.ogImage,
                            },
                            seoSiteProfile
                        ),
                    },
                    {
                        id: "lead-generation-sydney-breadcrumbs-json-ld",
                        schema: buildBreadcrumbSchema(getBreadcrumbSeoItems(breadcrumbItems), seoSiteProfile),
                    },
                ]}
            />
            <section className="w-full py-10 md:py-14" style={{ backgroundColor: pageBackgroundColor }}>
                <PageWithSidebarLayout
                    maxWidth="xl"
                    gridTemplateColumns={{
                        lg: "2fr 1fr",
                        xl: "2fr 1fr",
                    }}
                    main={<LeadGenerationMainContent />}
                    sidebar={<LeadGenerationSidebar />}
                />
                <div id="lead-generation-plan" className="mx-auto mt-10 max-w-3xl px-4 md:mt-12">
                    <div className="border-t border-black/10 pt-8 md:pt-10">
                        <ServiceContactForm content={leadGenerationBottomContactFormContent} />
                    </div>
                </div>
            </section>
        </>
    );
}
